import { Link } from 'react-router-dom';
import { Card, CardHeader, CardBody, Table, TableHeader, TableBody, TableRow, TableHead, TableCell } from '@/components/ui';
import censusData from '../../data/census/all-counties.json';
import zillowStates from '../../data/parsed/zillow-state-summary.json';
import mortgageRates from '../../data/economic/mortgage-rates.json';
import travisData from '../../data/travis-cad/properties.json';

export default function DashboardPage() {
  const counties = censusData as any[];
  const states = zillowStates as any[];
  const rates = mortgageRates as any[];
  const properties = travisData as any[];

  const totalCounties = counties.length;
  const totalStates = states.length;
  const totalProperties = properties.length;
  const totalMortgageRecords = rates.length;

  const nationalAvgValue = counties.reduce((sum, c) => sum + (c.medianValue || 0) * (c.housingUnits || 0), 0) / 
    counties.reduce((sum, c) => sum + (c.housingUnits || 0), 0);

  const latestRate = rates[rates.length - 1]?.rate || 0;
  const rateDate = rates[rates.length - 1]?.date || '';

  const topCounties = [...counties].sort((a, b) => b.medianValue - a.medianValue).slice(0, 10);
  const topStates = [...states].sort((a, b) => b.latestValue - a.latestValue).slice(0, 10);

  const formatCurrency = (v: number) => new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD', maximumFractionDigits: 0 }).format(v);
  const formatNumber = (v: number) => new Intl.NumberFormat('en-US').format(v);

  return (
    <div className="space-y-8">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold text-gray-900">Property Intelligence Dashboard</h1>
          <p className="text-gray-600 mt-1">Real-time property data across {formatNumber(totalCounties)} US counties</p>
        </div>
        <Link to="/search">
          <button className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700">Search Data</button>
        </Link>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
        <Card className="bg-blue-50 border-blue-200">
          <CardBody>
            <p className="text-blue-600 text-sm font-medium">US Counties</p>
            <p className="text-3xl font-bold text-blue-900 mt-2">{formatNumber(totalCounties)}</p>
            <p className="text-blue-700 text-sm">With property data</p>
          </CardBody>
        </Card>

        <Card className="bg-green-50 border-green-200">
          <CardBody>
            <p className="text-green-600 text-sm font-medium">National Avg Value</p>
            <p className="text-3xl font-bold text-green-900 mt-2">{formatCurrency(nationalAvgValue)}</p>
            <p className="text-green-700 text-sm">Median home value</p>
          </CardBody>
        </Card>

        <Card className="bg-purple-50 border-purple-200">
          <CardBody>
            <p className="text-purple-600 text-sm font-medium">30-Yr Mortgage Rate</p>
            <p className="text-3xl font-bold text-purple-900 mt-2">{latestRate}%</p>
            <p className="text-purple-700 text-sm">As of {rateDate}</p>
          </CardBody>
        </Card>

        <Card className="bg-orange-50 border-orange-200">
          <CardBody>
            <p className="text-orange-600 text-sm font-medium">Travis CAD Properties</p>
            <p className="text-3xl font-bold text-orange-900 mt-2">{formatNumber(totalProperties)}</p>
            <p className="text-orange-700 text-sm">Austin, TX scraped</p>
          </CardBody>
        </Card>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <Card>
          <CardHeader>
            <h3 className="text-lg font-semibold">🏆 Top 10 Counties by Value</h3>
          </CardHeader>
          <CardBody className="p-0">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>#</TableHead>
                  <TableHead>County</TableHead>
                  <TableHead className="text-right">Median Value</TableHead>
                  <TableHead className="text-right">Units</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {topCounties.map((county: any, idx: number) => (
                  <TableRow key={county.fips}>
                    <TableCell className="font-medium">{idx + 1}</TableCell>
                    <TableCell>{county.name?.split(',')[0]}</TableCell>
                    <TableCell className="text-right font-semibold text-green-600">{formatCurrency(county.medianValue)}</TableCell>
                    <TableCell className="text-right text-gray-500">{formatNumber(county.housingUnits)}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </CardBody>
        </Card>

        <Card>
          <CardHeader>
            <h3 className="text-lg font-semibold">🏆 Top 10 States by ZHVI</h3>
          </CardHeader>
          <CardBody className="p-0">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>#</TableHead>
                  <TableHead>State</TableHead>
                  <TableHead className="text-right">ZHVI</TableHead>
                  <TableHead className="text-right">Change</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {topStates.map((state: any, idx: number) => (
                  <TableRow key={state.state}>
                    <TableCell className="font-medium">{idx + 1}</TableCell>
                    <TableCell className="font-medium">{state.state}</TableCell>
                    <TableCell className="text-right font-semibold text-green-600">{formatCurrency(state.latestValue)}</TableCell>
                    <TableCell className={`text-right ${state.changePercent >= 0 ? 'text-green-600' : 'text-red-600'}`}>
                      +{state.changePercent?.toFixed(0)}%
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </CardBody>
        </Card>
      </div>

      <Card>
        <CardHeader>
          <h3 className="text-lg font-semibold">Texas Counties by Property Value</h3>
        </CardHeader>
        <CardBody className="p-0">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>#</TableHead>
                <TableHead>County</TableHead>
                <TableHead className="text-right">Median Value</TableHead>
                <TableHead className="text-right">Median Income</TableHead>
                <TableHead className="text-right">Housing Units</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {counties
                .filter((c: any) => c.stateFips === '48')
                .sort((a: any, b: any) => b.medianValue - a.medianValue)
                .slice(0, 20)
                .map((county: any, idx: number) => (
                  <TableRow key={county.fips}>
                    <TableCell className="font-medium">{idx + 1}</TableCell>
                    <TableCell>{county.name?.split(',')[0]}</TableCell>
                    <TableCell className="text-right font-semibold">{formatCurrency(county.medianValue)}</TableCell>
                    <TableCell className="text-right">{formatCurrency(county.medianIncome)}</TableCell>
                    <TableCell className="text-right text-gray-500">{formatNumber(county.housingUnits)}</TableCell>
                  </TableRow>
                ))}
            </TableBody>
          </Table>
        </CardBody>
      </Card>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
        <Card className="bg-blue-50 border-blue-200">
          <CardBody>
            <h4 className="font-semibold text-blue-900">📊 Census Bureau</h4>
            <p className="text-blue-700 text-sm mt-2">{formatNumber(totalCounties)} counties with median property values, income, and housing units.</p>
          </CardBody>
        </Card>
        <Card className="bg-green-50 border-green-200">
          <CardBody>
            <h4 className="font-semibold text-green-900">📈 Zillow Research</h4>
            <p className="text-green-700 text-sm mt-2">ZHVI time series for {formatNumber(totalStates)} states from 2000 to present.</p>
          </CardBody>
        </Card>
        <Card className="bg-purple-50 border-purple-200">
          <CardBody>
            <h4 className="font-semibold text-purple-900">🏦 FRED Data</h4>
            <p className="text-purple-700 text-sm mt-2">{formatNumber(totalMortgageRecords)} weekly 30-year mortgage rate records.</p>
          </CardBody>
        </Card>
      </div>
    </div>
  );
}
